import React, { useEffect, useState, useCallback } from "react";
import { api, fmt, fmtDate, formatApiError } from "../lib/api";
import { Truck, Plus, Trash2, Edit3, ChevronRight, BarChart2, Fuel } from "lucide-react";

function PageHeader() {
  return (
    <div className="mb-6">
      <div className="text-xs font-semibold uppercase tracking-widest text-gray-400 mb-1 flex items-center gap-1.5">
        <Truck size={11} /> Fleet
      </div>
      <h1 className="font-black text-3xl tracking-tight">Vehicles</h1>
      <p className="text-sm text-gray-500 mt-1">Fleet management &amp; diesel performance tracking</p>
    </div>
  );
}

function DevBadge({ pct }) {
  if (pct === null || pct === undefined) return <span className="text-gray-300 text-xs">—</span>;
  const abs = Math.abs(pct);
  if (abs < 15) return <span className="text-xs text-green-600 font-semibold">Normal</span>;
  if (pct < 0) return <span className="text-xs text-red-600 font-semibold">Low {abs.toFixed(1)}%</span>;
  return <span className="text-xs text-blue-600 font-semibold">High {abs.toFixed(1)}%</span>;
}

export function VehiclesPage() {
  const [vehicles, setVehicles] = useState([]);
  const [stats, setStats]       = useState({});
  const [loading, setLoading]   = useState(true);
  const [selected, setSelected] = useState(null); // vehicle id for stats panel
  const [statsData, setStatsData] = useState(null);
  const [loadingStats, setLoadingStats] = useState(false);

  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing]   = useState(null);
  const [form, setForm] = useState({ name: "", number: "", expected_avg_kmpl: "", notes: "", active: true });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get("/vehicles");
      setVehicles(r.data || []);
    } catch (err) { setError(formatApiError(err)); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const loadStats = async (vid) => {
    setLoadingStats(true);
    try {
      const r = await api.get(`/vehicles/${vid}/diesel-stats`);
      setStatsData(r.data);
    } catch (err) { setError(formatApiError(err)); }
    finally { setLoadingStats(false); }
  };

  const openAdd = () => {
    setEditing(null);
    setForm({ name: "", number: "", expected_avg_kmpl: "", notes: "", active: true });
    setShowForm(true);
  };

  const openEdit = (v) => {
    setEditing(v);
    setForm({ name: v.name, number: v.number, expected_avg_kmpl: String(v.expected_avg_kmpl || ""), notes: v.notes || "", active: v.active });
    setShowForm(true);
  };

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = { ...form, expected_avg_kmpl: parseFloat(form.expected_avg_kmpl || 0) };
      if (editing) {
        await api.patch(`/vehicles/${editing.id}`, payload);
      } else {
        await api.post("/vehicles", payload);
      }
      setShowForm(false); setEditing(null); await load();
    } catch (err) { setError(formatApiError(err)); }
    finally { setSaving(false); }
  };

  const deleteV = async (id) => {
    if (!window.confirm("Delete this vehicle?")) return;
    try { await api.delete(`/vehicles/${id}`); await load(); if (selected === id) { setSelected(null); setStatsData(null); } }
    catch (err) { setError(formatApiError(err)); }
  };

  const toggleStats = (vid) => {
    if (selected === vid) { setSelected(null); setStatsData(null); }
    else { setSelected(vid); loadStats(vid); }
  };

  return (
    <div className="px-6 py-6 max-w-5xl">
      <PageHeader />
      {error && (
        <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700 flex justify-between">
          <span>{error}</span><button onClick={() => setError("")} className="font-bold ml-4">×</button>
        </div>
      )}

      {/* Add button */}
      <div className="mb-4 flex justify-end">
        <button onClick={openAdd} className="flex items-center gap-2 px-4 py-2 bg-gray-900 text-white rounded-lg text-sm font-semibold hover:bg-gray-700 transition-colors" data-testid="add-vehicle-btn">
          <Plus size={14} /> Add Vehicle
        </button>
      </div>

      {/* Form */}
      {showForm && (
        <form onSubmit={save} className="mb-6 bg-white border border-gray-200 rounded-xl p-4 space-y-3">
          <h3 className="font-bold text-sm text-gray-800">{editing ? "Edit Vehicle" : "Add Vehicle"}</h3>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            <div>
              <label className="block text-xs text-gray-500 mb-1">Vehicle Name *</label>
              <input value={form.name} onChange={e => setForm(f => ({...f, name: e.target.value}))} required placeholder="e.g. Tata 407"
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-blue-500" data-testid="veh-name" />
            </div>
            <div>
              <label className="block text-xs text-gray-500 mb-1">Reg. Number *</label>
              <input value={form.number} onChange={e => setForm(f => ({...f, number: e.target.value.toUpperCase()}))} required placeholder="MH04AB1234"
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-blue-500" data-testid="veh-number" />
            </div>
            <div>
              <label className="block text-xs text-gray-500 mb-1">Exp. Avg (kmpl)</label>
              <input type="number" step="0.1" min="0" value={form.expected_avg_kmpl} onChange={e => setForm(f => ({...f, expected_avg_kmpl: e.target.value}))} placeholder="0.0"
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-blue-500" data-testid="veh-avg" />
            </div>
            <div>
              <label className="block text-xs text-gray-500 mb-1">Status</label>
              <select value={form.active ? "true" : "false"} onChange={e => setForm(f => ({...f, active: e.target.value === "true"}))}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-blue-500">
                <option value="true">Active</option>
                <option value="false">Inactive</option>
              </select>
            </div>
          </div>
          <div>
            <label className="block text-xs text-gray-500 mb-1">Notes</label>
            <input value={form.notes} onChange={e => setForm(f => ({...f, notes: e.target.value}))} placeholder="Optional notes"
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-blue-500" />
          </div>
          <div className="flex gap-2">
            <button type="submit" disabled={saving} className="bg-gray-900 text-white rounded-lg px-5 py-2 text-sm font-semibold hover:bg-gray-700 disabled:opacity-50" data-testid="save-vehicle-btn">
              {saving ? "Saving…" : editing ? "Update" : "Add Vehicle"}
            </button>
            <button type="button" onClick={() => { setShowForm(false); setEditing(null); }} className="text-sm text-gray-500 hover:text-gray-700 px-3">Cancel</button>
          </div>
        </form>
      )}

      {/* Vehicles list */}
      {loading ? (
        <div className="py-12 text-center text-sm text-gray-400">Loading vehicles…</div>
      ) : vehicles.length === 0 ? (
        <div className="py-12 text-center bg-white border border-gray-200 rounded-xl">
          <Truck size={28} className="mx-auto mb-3 text-gray-300" />
          <div className="text-sm text-gray-500 font-medium">No vehicles added yet</div>
          <div className="text-xs text-gray-400 mt-1">Add your first vehicle to start tracking diesel</div>
        </div>
      ) : (
        <div className="space-y-3">
          {vehicles.map(v => (
            <div key={v.id} className="bg-white border border-gray-200 rounded-xl overflow-hidden" data-testid={`vehicle-row-${v.id}`}>
              <div className="flex items-center justify-between px-4 py-3">
                <div className="flex items-center gap-3">
                  <div className={`w-8 h-8 rounded-full flex items-center justify-center ${v.active ? "bg-gray-900" : "bg-gray-200"}`}>
                    <Truck size={14} className={v.active ? "text-white" : "text-gray-400"} />
                  </div>
                  <div>
                    <div className="font-bold text-gray-900 text-sm">{v.name}</div>
                    <div className="text-xs text-gray-500 font-mono">{v.number}</div>
                  </div>
                </div>
                <div className="flex items-center gap-3 text-xs text-gray-500">
                  {v.expected_avg_kmpl > 0 && (
                    <span className="flex items-center gap-1"><Fuel size={11} /> {v.expected_avg_kmpl} kmpl</span>
                  )}
                  {!v.active && <span className="text-gray-400 bg-gray-100 px-2 py-0.5 rounded-full">Inactive</span>}
                </div>
                <div className="flex items-center gap-2">
                  <button onClick={() => toggleStats(v.id)} className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-gray-600 bg-gray-50 hover:bg-gray-100 rounded-lg border border-gray-200 transition-colors" data-testid={`stats-btn-${v.id}`}>
                    <BarChart2 size={12} /> {selected === v.id ? "Hide Stats" : "Stats"}
                  </button>
                  <button onClick={() => openEdit(v)} className="p-1.5 hover:bg-gray-100 rounded-lg" title="Edit"><Edit3 size={13} /></button>
                  <button onClick={() => deleteV(v.id)} className="p-1.5 hover:bg-red-50 text-gray-300 hover:text-red-500 rounded-lg" title="Delete"><Trash2 size={13} /></button>
                </div>
              </div>

              {/* Stats panel */}
              {selected === v.id && (
                <div className="border-t border-gray-100 bg-gray-50/50 px-4 py-3">
                  {loadingStats ? (
                    <div className="text-sm text-gray-400 text-center py-4">Loading diesel stats…</div>
                  ) : statsData ? (
                    <>
                      {/* Lifetime summary */}
                      <div className="grid grid-cols-4 gap-4 mb-4 text-center">
                        {[
                          { label: "Total KM", val: statsData.lifetime?.km?.toLocaleString("en-IN") ?? "—" },
                          { label: "Total Litres", val: statsData.lifetime?.litres?.toFixed(1) ?? "—" },
                          { label: "Lifetime Avg", val: statsData.lifetime?.avg_kmpl ? `${statsData.lifetime.avg_kmpl} kmpl` : "—" },
                          { label: "Deviation", val: statsData.lifetime?.deviation_pct != null ? `${statsData.lifetime.deviation_pct > 0 ? "+" : ""}${statsData.lifetime.deviation_pct}%` : "—" },
                        ].map(s => (
                          <div key={s.label} className="bg-white rounded-lg border border-gray-200 p-2">
                            <div className="text-xs text-gray-400 mb-1">{s.label}</div>
                            <div className="font-bold text-sm text-gray-800">{s.val}</div>
                          </div>
                        ))}
                      </div>
                      {/* Run table */}
                      <div className="overflow-x-auto rounded-lg border border-gray-200">
                        <table className="w-full text-xs">
                          <thead>
                            <tr className="bg-gray-100 border-b border-gray-200">
                              {["Date","Odometer (km)","KM Run","Litres","Actual Avg","Dev %","Status","Notes"].map(h => (
                                <th key={h} className="px-3 py-2 text-left text-gray-500 font-semibold whitespace-nowrap">{h}</th>
                              ))}
                            </tr>
                          </thead>
                          <tbody>
                            {(statsData.runs || []).map((run, i) => (
                              <tr key={i} className="border-b border-gray-100 bg-white hover:bg-gray-50">
                                <td className="px-3 py-2 text-gray-600">{fmtDate(run.date)}</td>
                                <td className="px-3 py-2 font-mono">{run.odometer_km?.toLocaleString("en-IN") ?? "—"}</td>
                                <td className="px-3 py-2 font-mono">{run.km_run != null ? run.km_run.toLocaleString("en-IN") : "—"}</td>
                                <td className="px-3 py-2 font-mono">{run.litres?.toFixed(1) ?? "—"}</td>
                                <td className="px-3 py-2 font-mono">{run.actual_avg_kmpl != null ? `${run.actual_avg_kmpl} kmpl` : "—"}</td>
                                <td className="px-3 py-2 font-mono">{run.deviation_pct != null ? `${run.deviation_pct > 0 ? "+" : ""}${run.deviation_pct}%` : "—"}</td>
                                <td className="px-3 py-2"><DevBadge pct={run.deviation_pct} /></td>
                                <td className="px-3 py-2 text-gray-400 max-w-[120px] truncate">{run.description}</td>
                              </tr>
                            ))}
                            {(!statsData.runs || statsData.runs.length === 0) && (
                              <tr><td colSpan={8} className="px-3 py-4 text-center text-gray-400">No diesel entries yet for this vehicle</td></tr>
                            )}
                          </tbody>
                        </table>
                      </div>
                    </>
                  ) : null}
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export default VehiclesPage;
